import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";

const API = "/api";

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("Invalid verification link. No token provided.");
      return;
    }
    const verify = async () => {
      try {
        const { data } = await axios.get(`${API}/auth/verify-email?token=${token}`);
        setStatus("success");
        setMessage(data.message);
      } catch (err) {
        setStatus("error");
        setMessage(err.response?.data?.detail || "Verification failed. The link may be invalid or expired.");
      }
    };
    verify();
  }, [token]);

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12 animate-fade-in" data-testid="verify-email-page">
      <Card className="w-full max-w-md border-border rounded-sm">
        <CardContent className="pt-8 pb-8 text-center">
          {status === "loading" && (
            <div data-testid="verify-email-loading">
              <Loader2 className="h-12 w-12 text-muted-foreground mx-auto mb-4 animate-spin" />
              <h2 className="font-heading text-xl font-bold mb-2">Verifying your email...</h2>
              <p className="text-sm text-muted-foreground">Please wait a moment.</p>
            </div>
          )}
          {status === "success" && (
            <div data-testid="verify-email-success">
              <CheckCircle className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
              <h2 className="font-heading text-xl font-bold mb-2">Email Verified</h2>
              <p className="text-sm text-muted-foreground mb-6">{message}</p>
              <Link to="/login">
                <Button className="rounded-sm" data-testid="verify-go-to-login-btn">Go to Login</Button>
              </Link>
            </div>
          )}
          {status === "error" && (
            <div data-testid="verify-email-error">
              <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="font-heading text-xl font-bold mb-2">Verification Failed</h2>
              <p className="text-sm text-muted-foreground mb-6">{message}</p>
              <Link to="/login">
                <Button variant="outline" className="rounded-sm" data-testid="verify-back-to-login-btn">Back to Login</Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
